import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  FiDroplet,
  FiTool,
  FiShoppingBag,
  FiUsers,
  FiTruck,
  FiBriefcase,
  FiClipboard,
  FiBarChart2,
  FiPackage,
  FiSettings,
  FiChevronsRight
} from "react-icons/fi";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const ModuleSection = () => {
  const { t } = useTranslation();
  const [selectedIndustry, setSelectedIndustry] = useState("Residential");
  const industries = [
    "Residential",
    "Hotels",
    "Spa",
    "Retail",
    "Wholesale",
    "Commercial",
    "General"
  ];

  const modules = [
    {
      icon: <FiSettings size={20} />,
      titleKey: "modules.construction.title",
      descKey: "modules.construction.desc",
      link: "pool-construction",
      industryTags: ["Residential", "Hotels", "Spa"],
      example: t("modules.construction.example")
    },
    {
      icon: <FiTool size={20} />,
      titleKey: "modules.installation.title",
      descKey: "modules.installation.desc",
      link: "installation-services",
      industryTags: ["Residential", "Commercial"],
      example: t("modules.installation.example")
    },
    {
      icon: <FiPackage size={20} />,
      titleKey: "modules.equipment.title",
      descKey: "modules.equipment.desc",
      link: "equipment-accessories",
      industryTags: ["Retail", "Wholesale"],
      example: t("modules.equipment.example")
    },
    {
      icon: <FiTruck size={20} />,
      titleKey: "modules.logistics.title",
      descKey: "modules.logistics.desc",
      link: "logistics-delivery",
      industryTags: ["Construction", "Retail"],
      example: t("modules.logistics.example")
    },
    {
      icon: <FiClipboard size={20} />,
      titleKey: "modules.maintenance.title",
      descKey: "modules.maintenance.desc",
      link: "maintenance-scheduling",
      industryTags: ["Residential", "Commercial"],
      example: t("modules.maintenance.example")
    },
    {
      icon: <FiDroplet size={20} />,
      titleKey: "modules.treatment.title",
      descKey: "modules.treatment.desc",
      link: "water-treatment",
      industryTags: ["Spa", "Hotels", "Residential"],
      example: t("modules.treatment.example")
    },
    {
      icon: <FiShoppingBag size={20} />,
      titleKey: "modules.retail.title",
      descKey: "modules.retail.desc",
      link: "retail-sales",
      industryTags: ["Retail"],
      example: t("modules.retail.example")
    },
    {
      icon: <FiUsers size={20} />,
      titleKey: "modules.crm.title",
      descKey: "modules.crm.desc",
      link: "client-relationship",
      industryTags: ["General"],
      example: t("modules.crm.example")
    },
    {
      icon: <FiBriefcase size={20} />,
      titleKey: "modules.invoicing.title",
      descKey: "modules.invoicing.desc",
      link: "quotations-invoicing",
      industryTags: ["All"],
      example: t("modules.invoicing.example")
    }
  ];

  useEffect(() => {
    const stored = localStorage.getItem("preferredIndustry");
    if (stored) setSelectedIndustry(stored);
  }, []);

  useEffect(() => {
    localStorage.setItem("preferredIndustry", selectedIndustry);
  }, [selectedIndustry]);

  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1.1,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.2
        }
      }
    ]
  };

  const renderModule = (mod, i) => (
    <motion.div
      key={i}
      className={`flex flex-col justify-between space-y-2 text-left border p-4 rounded-lg transition-all relative group h-full mt-6 ${
        mod.industryTags?.includes(selectedIndustry)
          ? "border-primary bg-primary/5"
          : "border-gray-200"
      }`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: i * 0.1 }}
      viewport={{ once: true }}
    >
      <div>
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary/10 rounded-full text-primary">{mod.icon}</div>
          <h3 className="font-bold text-gray-800">
            <Link to={`/modules/${mod.link}`} className="hover:text-primary">
              {t(mod.titleKey)}
            </Link>
          </h3>
        </div>
        <p className="text-sm text-gray-600 pl-10">{t(mod.descKey)}</p>
      </div>
      <div className="absolute bottom-2 right-2 text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
        {mod.example}
      </div>
    </motion.div>
  );

  return (
    <section className="bg-gray-50 py-20 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <motion.h2
              className="text-3xl font-bold text-gray-900 mb-2 text-left"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <span className="text-primary">TriTech</span> {t("modules.sectionTitle")}
            </motion.h2>
            <motion.p
              className="text-gray-600 text-left"
              initial={{ opacity: 0, y: -10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {t("modules.sectionSubtitle")}
            </motion.p>
          </div>

          <motion.div
            className="text-left md:text-right w-full md:w-auto"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <label className="mr-2 font-medium">{t("modules.filterLabel")}</label>
            <select
              className="border border-gray-300 px-4 py-2 rounded focus:outline-none"
              value={selectedIndustry}
              onChange={(e) => setSelectedIndustry(e.target.value)}
            >
              {industries.map((ind, idx) => (
                <option key={idx} value={ind}>
                  {ind}
                </option>
              ))}
            </select>
          </motion.div>
        </div>

        <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10">
          {modules.map(renderModule)}
        </div>

        <div className="md:hidden">
          <Slider {...sliderSettings}>{modules.map(renderModule)}</Slider>
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-primary text-white rounded-xl px-6 max-w-7xl mx-auto shadow relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent z-0" />
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 relative z-10">
            <div className="text-center md:text-left flex-1 py-12">
              <h3 className="text-2xl font-bold mb-2">
                {t("modules.ctaTitle")}
              </h3>
              <p className="text-sm mb-4">
                {t("modules.ctaSubtitle")}
              </p>

              <div className="mt-6 flex flex-wrap justify-center md:justify-start gap-4 text-white text-sm">
                <div className="flex items-center space-x-2"><FiSettings /><span>{t("modules.ctaItems.construction")}</span></div>
                <div className="flex items-center space-x-2"><FiTool /><span>{t("modules.ctaItems.installation")}</span></div>
                <div className="flex items-center space-x-2"><FiPackage /><span>{t("modules.ctaItems.accessories")}</span></div>
                <div className="flex items-center space-x-2"><FiDroplet /><span>{t("modules.ctaItems.water")}</span></div>
                <div className="flex items-center space-x-2"><FiChevronsRight /><span>{t("modules.ctaItems.more")}</span></div>
              </div>

              <div className="py-4 mt-4 flex flex-wrap justify-center md:justify-start gap-4 text-white text-sm">
                <a
                  href="/contact"
                  className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
                >
                  {t("modules.ctaButton")}
                </a>
              </div>
            </div>

            <div className="hidden md:block flex-1 h-full absolute right-0 top-0">
              <img
                src="/assets/images/construction-man.png"
                alt="Pools Illustration"
                className="absolute bottom-0 right-0 max-w-none w-[200px] object-contain animate-float"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModuleSection;
